import {
  ExclamationCircleOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import {
  Divider,
  Flex,
  Modal,
  Popover,
  Tooltip,
} from 'antd'
import classNames from 'classnames'
import * as React from 'react'
import {
  useMemo,
  useState,
} from 'react'

import s from './more-options-modal.module.css'

export interface MoreOption {
  type?: 'action' | 'divider'
  name?: React.ReactNode
  copyText?: string
  tooltip?: string
  danger?: boolean
  onClick?: () => void
}

export interface MoreOptionsModalProps {
  header?: React.ReactNode
  options?: MoreOption[]
  type?: 'modal' | 'menu'
}

export const moreOptionDivider: MoreOption = { type: 'divider' } as const

export default function MoreOptionsModal(
  { options, header, type = 'modal' }: MoreOptionsModalProps,
) {
  const [open, setOpen] = useState(false)

  const onOptionClicked = React.useCallback((option: MoreOption) => {
    option.onClick?.()
    setOpen(false)
  }, [])

  const content = useMemo(() => {
    return (
      <Flex vertical>
        {header}
        {header && <Divider className={s.Divider} />}
        {options?.map((opt, idx) => {
          if (opt.type === 'divider') {
            return <Divider className={s.Divider} key={idx} />
          }
          return (
            <Flex key={idx} justify="space-between">
              <div className={classNames(s.OptionItem, { [s.Danger]: opt.danger })}>
                {typeof opt.name === 'string' ? (
                  <a onClick={() => onOptionClicked(opt)}>
                    {opt.name}
                  </a>
                ) : (
                  <div>{opt.name}</div>
                )}
              </div>
              {opt.tooltip && (
                <Tooltip title={opt.tooltip}>
                  <ExclamationCircleOutlined />
                </Tooltip>
              )}
            </Flex>
          )
        })}
      </Flex>
    )
  }, [header, onOptionClicked, options])

  return (
    <div>
      <MoreOutlined className="icon-button" onClick={() => setOpen(true)} />
      {type === 'modal' ?
        <Modal
          open={open}
          getContainer={false}
          destroyOnClose
          width={240}
          classNames={{ body: s.ModalContent }}
          closeIcon={null}
          onCancel={() => setOpen(false)}
          footer={null}
          centered
        >
          {content}
        </Modal>
        :
        <Popover
          trigger="click"
          placement="bottomRight"
          open={open}
          onOpenChange={setOpen}
          content={content}
        ></Popover>
      }
    </div>
  )
}
